export const API_BASE = (import.meta.env?.VITE_API_BASE_URL || 'https://talkup-api.gelium123.workers.dev').replace(/\/$/, '');

export interface Actor { id: string; role: 'student' | 'teacher' | 'admin'; }
export interface Pair { id: string; teacher_id: string; student_one_id: string; student_two_id: string | null; name: string; course_id: string | null; }
export interface User { id: string; role: 'student' | 'teacher' | 'admin'; name: string; email: string | null; created_at: string; }

function readStoredToken(): string | null {
  try { return window.localStorage.getItem('talkup_token'); }
  catch { return null; }
}

let token = readStoredToken();

export function setToken(value: string | null) {
  token = value;
  try {
    if (value) window.localStorage.setItem('talkup_token', value);
    else window.localStorage.removeItem('talkup_token');
  } catch {
    // Safari private browsing / embedded browsers can restrict storage.
    // Keep the session token in memory so the app still renders and works.
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', 'Bearer ' + token);
  const response = await fetch(API_BASE + path, { ...init, headers });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error?.message || 'TalkUp API request failed.');
  }
  return payload.data as T;
}

export async function login(email: string, password: string) {
  const data = await request<{ token: string; actor: Actor }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setToken(data.token);
  return data.actor;
}

export async function me() { return request<Actor>('/api/me'); }
export async function logout() {
  try { await request('/api/auth/logout', { method: 'POST' }); } finally { setToken(null); }
}
export async function pairs() { return request<Pair[]>('/api/pairs'); }
export async function unlock(lessonId: string, pairId: string, studentId?: string | null) {
  return request('/api/access/unlock', {
    method: 'POST',
    body: JSON.stringify({ lesson_id: lessonId, pair_id: pairId, student_id: studentId ?? null }),
  });
}
export async function users() { return request<User[]>('/api/users'); }

export interface TeacherCourseAccess {
  teacher_id: string;
  course_id: string;
  unlocked: number;
  updated_at: string;
}
export interface AdminTeacherAccess {
  teachers: Array<{ id: string; name: string; email: string | null; created_at: string }>;
  courses: Course[];
  access: TeacherCourseAccess[];
}
export async function adminTeacherAccess() {
  return request<AdminTeacherAccess>('/api/admin/teacher-access');
}
export async function setTeacherCourseAccess(teacherId: string, courseId: string, unlocked: boolean) {
  return request('/api/admin/teacher-access', {
    method: 'POST',
    body: JSON.stringify({ teacher_id: teacherId, course_id: courseId, unlocked }),
  });
}
export async function createUser(input: { id: string; role: 'student' | 'teacher' | 'admin'; name: string; email: string; password: string }) {
  return request('/api/users', { method: 'POST', body: JSON.stringify(input) });
}
export async function createPair(input: { id: string; teacher_id: string; student_one_id: string; student_two_id?: string | null; name: string; course_id: string }) {
  return request('/api/pairs', { method: 'POST', body: JSON.stringify(input) });
}
export async function courses() { return request<Course[]>('/api/courses'); }
export async function lessons(courseId: string) { return request<LessonSummary[]>('/api/lessons?course_id=' + encodeURIComponent(courseId)); }

export async function bootstrapAdmin(bootstrapToken: string, id: string, name: string, email: string, password: string) {
  const body = new URLSearchParams({ bootstrap_token: bootstrapToken, id, name, email, password });
  let response: Response;
  try {
    response = await fetch(API_BASE + '/api/auth/bootstrap', {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
    });
  } catch {
    throw new Error('Could not connect to TalkUp API. Please refresh the page and try again.');
  }
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error?.message || ('Bootstrap failed (HTTP ' + response.status + ').'));
  }
  return payload.data as { id: string; role: 'admin'; email: string };
}

export interface Course { id: string; language: string; level: string; title: string; description: string | null; }
export interface LessonSummary { id: string; course_id: string; unit_number: number; lesson_number: number; title: string; goals_json: string; block_count?: number; }
export interface LessonBlockData { id: string; lesson_id: string; block_order: number; block_type: string; title: string; payload_json: string; source: string; content_json?: string | null; }
export interface ReadyLessonStep {
  plan_id: string;
  lesson_id: string;
  duration_minutes: number;
  language: string;
  level: string;
  sequence_rank: number;
  profile: string;
  unit_number: number;
  lesson_number: number;
  lesson_title: string;
  lesson_description: string | null;
  step_order: number;
  minutes: number;
  stage_key: string;
  stage_title: string;
  teacher_instruction: string;
  block_id: string | null;
  block_type: string | null;
  block_title: string | null;
  payload_json: string | null;
  source: string | null;
}
export async function readyLessons(courseId?: string) {
  return request<ReadyLessonStep[]>(courseId ? `/api/ready-lessons?course_id=${encodeURIComponent(courseId)}` : '/api/ready-lessons');
}

export async function lessonBlocks(lessonId: string) { return request<LessonBlockData[]>('/api/lesson-blocks?lesson_id=' + encodeURIComponent(lessonId)); }
export async function reorderLessonBlocks(lessonId: string, blockIds: string[]) {
  return request('/api/lesson-blocks/order', { method: 'PATCH', body: JSON.stringify({ lesson_id: lessonId, block_ids: blockIds }) });
}

export async function accessibleLessons(courseId: string) { return request<(LessonSummary & { unlocked: number })[]>('/api/accessible-lessons?course_id=' + encodeURIComponent(courseId)); }

export interface HomeworkItem {
  id: string;
  lesson_id: string;
  pair_id: string | null;
  student_id: string | null;
  title: string;
  instructions: string;
  kind: string;
  due_at: string | null;
  status: string;
  submission_id: string | null;
  submission_status: string | null;
  submitted_at: string | null;
  feedback: string | null;
}
export interface VocabularyItem {
  id: string;
  student_id: string;
  lesson_id: string | null;
  word: string;
  translation: string;
  language: string;
  level: string | null;
  topic: string | null;
  example: string | null;
  pronunciation: string | null;
  audio_key: string | null;
  review_state: 'new' | 'learning' | 'mastered' | string;
  due_at: string | null;
  interval_days: number;
  repetitions: number;
}
export interface VocabularyBankItem extends Omit<VocabularyItem, 'id' | 'student_id' | 'review_state' | 'due_at' | 'interval_days' | 'repetitions'> { id: string; sort_order: number; }
export async function vocabulary(studentId: string) { return request<VocabularyItem[]>('/api/vocabulary?student_id=' + encodeURIComponent(studentId)); }
export async function vocabularyBank(lessonId: string) { return request<VocabularyBankItem[]>('/api/vocabulary-bank?lesson_id=' + encodeURIComponent(lessonId)); }
export async function addVocabulary(input: { word: string; translation: string; language: string; lesson_id?: string | null; level?: string | null; topic?: string | null; example?: string | null; pronunciation?: string | null }) {
  return request('/api/vocabulary', { method: 'POST', body: JSON.stringify(input) });
}
export async function reviewVocabulary(id: string, rating: 'again' | 'good' | 'easy') {
  return request('/api/vocabulary/review', { method: 'POST', body: JSON.stringify({ id, rating }) });
}

export async function homework(studentId: string) { return request<HomeworkItem[]>('/api/homework?student_id=' + encodeURIComponent(studentId)); }
export async function createHomework(input: { lesson_id: string; pair_id?: string | null; student_id?: string | null; title: string; instructions: string; kind: string; due_at?: string | null }) {
  return request('/api/homework', { method: 'POST', body: JSON.stringify(input) });
}
export async function submitHomework(input: { homework_id: string; answer?: unknown; audio_key?: string | null }) {
  return request('/api/submissions', { method: 'POST', body: JSON.stringify(input) });
}

export interface SubmissionItem { id: string; homework_id: string; student_id: string; answer_json: string | null; audio_key: string | null; status: string; feedback: string | null; reviewed_by: string | null; submitted_at: string; title: string; kind: string; pair_id: string | null; }
export async function submissions(pairId?: string) { return request<SubmissionItem[]>('/api/submissions' + (pairId ? '?pair_id=' + encodeURIComponent(pairId) : '')); }
export async function reviewSubmission(id: string, feedback: string, status = 'reviewed') {
  return request('/api/submissions/' + encodeURIComponent(id) + '/review', { method: 'POST', body: JSON.stringify({ feedback, status }) });
}

export interface ProgressItem {
  id: string;
  student_id: string;
  lesson_id: string;
  block_id: string | null;
  status: string;
  score: number | null;
  seconds_spent: number;
  updated_at: string;
}
export async function progress(studentId: string) { return request<ProgressItem[]>('/api/progress?student_id=' + encodeURIComponent(studentId)); }

export async function saveProgress(input: { student_id: string; lesson_id: string; block_id?: string | null; status: string; score?: number | null; seconds_spent?: number }) {
  return request('/api/progress', { method: 'POST', body: JSON.stringify(input) });
}

export async function createLessonBlock(input: { lesson_id: string; title: string; block_type: string; text?: string; payload?: Record<string, unknown> }) {
  return request('/api/lesson-blocks', { method: 'POST', body: JSON.stringify(input) });
}

export interface TeachingOverview {
  pair: Pair;
  students: Array<{ id: string; name: string; email: string | null }>;
  lessons: LessonSummary[];
  progress: Array<{ student_id: string; lesson_id: string; block_id: string | null; status: string; score: number | null; seconds_spent: number; updated_at: string; unit_number: number; lesson_number: number; title: string; block_title: string | null }>;
  homework: Array<{ id: string; student_id: string | null; title: string; kind: string; due_at: string | null; status: string; submission_id: string | null; submission_status: string | null; submitted_at: string | null; feedback: string | null }>;
}
export async function teachingOverview(pairId: string) {
  return request<TeachingOverview>('/api/teaching-overview?pair_id=' + encodeURIComponent(pairId));
}

export async function naturalVoice(text: string, language: 'de' | 'en') {
  const token = localStorage.getItem('talkup_token');
  const response = await fetch(API_BASE + '/api/tts?language=' + language + '&text=' + encodeURIComponent(text), {
    headers: token ? { authorization: 'Bearer ' + token } : {},
  });
  if (!response.ok) throw new Error('Natural voice unavailable');
  return URL.createObjectURL(await response.blob());
}
